import { FormControl, VFlow } from 'bold-ui'
import { Form, FormRenderProps, TextAreaField } from 'components/form'
import {
  ProcedimentoOdontoSelectField,
  ProcedimentoOdontoSelectModel,
} from 'components/form/field/select/ProcedimentoOdontoSelectField'
import createDecorator from 'final-form-calculate'
import { ParteBucalEnum, TipoParteBucalEnum } from 'graphql/types.generated'
import React, { useEffect, useState } from 'react'
import { metaPath } from 'util/metaPath'

import CondicoesDenteField, { CondicoesDenteFieldModel } from '../components/CondicoesDenteField'
import OdontogramaPopper, { OdontogramaPopperProps } from '../components/OdontogramaPopper'
import ReplicarItemSelectField, {
  convertParteBucalEnumToReplicarItem,
  ReplicarItemSelectFieldModel,
} from '../components/ReplicarItemSelectField'
import { getDentes, getDentesComDados, getParteBucal, isDenteDeciduo } from '../util'
import { DenteFieldModel } from './DentesField'

export interface DenteFormPopperProps extends Omit<OdontogramaPopperProps, 'title' | 'onClose'> {
  parteBucalEnum: ParteBucalEnum
  cidadaoDataNascimento: LocalDate
  dentesValues: Record<ParteBucalEnum, DenteFieldModel>
  protese?: boolean
  initialValues?: DenteFieldModel
  proteseTotalSuperior?: boolean
  proteseTotalInferior?: boolean
  onSubmit?(values: DenteFieldModel): void
}

export interface DenteFormModel extends DenteFieldModel {
  replicarItems?: ReplicarItemSelectFieldModel[]
}

const meta = metaPath<DenteFormModel>()

const denteFormDecorator = createDecorator(
  {
    field: meta.procedimentos.absolutePath(),
    updates: {
      [meta.observacoes.absolutePath()]: (value: ProcedimentoOdontoSelectModel[], allValues: DenteFormModel) => {
        if (value?.length > 0) return allValues.observacoes
      },
    },
  },
  {
    field: meta.condicoesDente.absolutePath(),
    updates: {
      [meta.replicarItems.absolutePath()]: (value: CondicoesDenteFieldModel[], allValues: DenteFormModel) => {
        if (value?.length > 0) return allValues.replicarItems
      },
    },
  }
)

export default function DenteFormPopper(props: DenteFormPopperProps) {
  const {
    parteBucalEnum,
    initialValues,
    cidadaoDataNascimento,
    protese,
    dentesValues,
    proteseTotalSuperior,
    proteseTotalInferior,
    onSubmit,
    ...popperProps
  } = props

  const [itemsDisabled, setItemsDisabled] = useState<ParteBucalEnum[]>()
  const [replicarItems, setReplicarItems] = useState<ReplicarItemSelectFieldModel[]>()
  const dente = getParteBucal(parteBucalEnum)

  useEffect(() => {
    setReplicarItems(
      convertParteBucalEnumToReplicarItem(getDentes()).filter(
        (item) => item.value !== parteBucalEnum && isDenteDeciduo(item.value) === isDenteDeciduo(parteBucalEnum)
      )
    )
  }, [parteBucalEnum])

  useEffect(() => {
    dentesValues && setItemsDisabled(getDentesComDados(dentesValues, proteseTotalSuperior, proteseTotalInferior))
  }, [dentesValues, proteseTotalInferior, proteseTotalSuperior])

  const renderForm = (formProps: FormRenderProps<DenteFormModel>) => {
    const temProcedimentos = !!formProps?.values?.procedimentos?.length
    const temCondicoes = !!formProps?.values?.condicoesDente?.length

    return (
      <OdontogramaPopper title={dente.title} onClose={formProps.handleSubmit} {...popperProps}>
        <VFlow>
          <CondicoesDenteField name={meta.condicoesDente} parteBucalEnum={parteBucalEnum} protese={protese} />

          <ReplicarItemSelectField
            label='Replicar problemas/condições para outros dentes'
            name={meta.replicarItems}
            items={replicarItems}
            itemsDisabled={itemsDisabled}
            disabled={!temCondicoes}
          />

          <ProcedimentoOdontoSelectField
            name={meta.procedimentos}
            label='Procedimentos (Evolução odontológica)'
            multiple
            dataNascimento={cidadaoDataNascimento}
            tipoParteBucal={TipoParteBucalEnum.DENTE}
            numeroDente={dente.number}
          />

          <FormControl label='Observações'>
            <TextAreaField
              name={meta.observacoes[0]}
              maxLength={400}
              placeholder='Insira informações adicionais sobre os procedimentos realizados'
              style={{ resize: 'none' }}
              disabled={!temProcedimentos}
            />
          </FormControl>
        </VFlow>
      </OdontogramaPopper>
    )
  }

  return (
    <Form<DenteFormModel>
      render={renderForm}
      onSubmit={onSubmit}
      initialValues={initialValues}
      decorators={[denteFormDecorator]}
    />
  )
}
